import { price as priceUtils } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { breakpoints, Stack, Text, textColors, Tooltip, vars } from '@etg/wings';
import { ScreenReaderPrice } from '@eti/components';
import { Info } from '@phosphor-icons/react';
import { screenReaderOnly } from '../../../common/utils/styles';
import { dataCellStyles, resetButtonStyles } from './styles';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const simpleCellStyles = css`
  ${dataCellStyles};

  @media (max-width: 47.9375em) {
    display: block;
    margin-left: 36px;
    max-width: 43%;

    html[dir='rtl'] & {
      margin-left: 0;
      margin-right: 36px;
    }
  }
`;
const breakdownCellStyles = css`
  ${dataCellStyles};

  @media (max-width: 47.9375em) {
    display: block;
    margin-left: 36px;
    max-width: 30%;

    html[dir='rtl'] & {
      margin-left: 0;
      margin-right: 36px;
    }
  }
`;
const priceStyles = css`
  display: flex;
`;
const labelStyle = css`
  margin-right: 3px;
`;
const infoIconButtonStyles = css`
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  margin-inline-start: 0.375rem;
  margin-top: -5px;

  &:focus-visible {
    border: 2px solid ${vars.colors.focusRing};
    transition: all 0.2s ease;
  }

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
    margin-top: -3px;
  }
`;
const seatPriceTextStyles = css`
  font-size: 0.625rem;

  @media (min-width: ${breakpoints._768}) {
    font-size: 0.875rem;
  }
`;
const SummaryPriceRow = props => {
  const {
    t
  } = useTranslation();
  if (typeof props.seatPrice !== 'number') {
    return /*#__PURE__*/_jsx("td", {});
  }
  if (props.carrier) {
    var _carrier$name;
    const {
      carrier,
      markup,
      seatPrice
    } = props;
    const normalizedMarkup = markup !== null && markup !== void 0 ? markup : 0;
    const formattedPrice = priceUtils.formatPrice(seatPrice);
    const markupPrice = priceUtils.formatPrice(normalizedMarkup);
    const providerPrice = priceUtils.formatPrice(seatPrice - normalizedMarkup);
    const carrierName = (_carrier$name = carrier.name) !== null && _carrier$name !== void 0 ? _carrier$name : '';
    const markupLabel = t('Product.Markup.Label');
    const pricebreakdownLabel = t('ScreenReader.Product.Pricebreakdown.Label');
    const seatLabel = t('Product.SeatMap.Seat.Price');
    const priceBreakdownHtml = t('Product.Carrier.PriceBreakdown.Text', `<bdi>${carrierName} ${providerPrice}</bdi>`, `<bdi>${markupLabel} ${markupPrice}</bdi>`);
    return /*#__PURE__*/_jsx("td", {
      className: breakdownCellStyles,
      children: /*#__PURE__*/_jsxs(Stack, {
        spacing: 4,
        children: [/*#__PURE__*/_jsxs(Text, {
          "aria-hidden": "true",
          className: seatPriceTextStyles,
          "data-testid": "summary-price-cell-price",
          children: [seatLabel, " ", formattedPrice]
        }), /*#__PURE__*/_jsx("span", {
          className: screenReaderOnly,
          children: seatLabel
        }), /*#__PURE__*/_jsx(ScreenReaderPrice, {
          price: formattedPrice
        }), carrierName && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            "aria-hidden": "true",
            asHtml: true,
            className: seatPriceTextStyles,
            color: textColors.SECONDARY,
            "data-testid": "summary-price-cell-price-breakdown-html",
            children: priceBreakdownHtml
          }), /*#__PURE__*/_jsx(Text, {
            asHtml: true,
            className: screenReaderOnly,
            "data-testid": "summary-price-cell-price-breakdown-text",
            children: `${pricebreakdownLabel}: ${priceBreakdownHtml}`
          })]
        })]
      })
    });
  }
  const {
    markup,
    tooltipId,
    seatPrice
  } = props;
  const isFirstTooltip = tooltipId === 'row_0_0_0';
  const seatLabel = t('Product.SeatMap.Seat.Price');
  const markupLabel = t('SeatMap.PriceInformation.Markup');
  const priceLabel = t('SeatMap.PriceInformation.Price');
  const providerCostLabel = t('SeatMap.PriceInformation.ProviderCost');
  const priceInfoTotalLabel = t('SeatMap.PriceInformation.TotalPrice');
  return /*#__PURE__*/_jsx("td", {
    className: simpleCellStyles,
    children: /*#__PURE__*/_jsxs("div", {
      className: priceStyles,
      children: [/*#__PURE__*/_jsx("span", {
        className: labelStyle,
        children: seatLabel
      }), priceUtils.formatPrice(seatPrice), typeof markup === 'number' && tooltipId && /*#__PURE__*/_jsx("div", {
        "data-testid": `priceInfoTooltip-${tooltipId}`,
        children: /*#__PURE__*/_jsx(Tooltip, {
          content: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs("div", {
              children: [markupLabel, ": ", priceUtils.formatPrice(markup)]
            }), /*#__PURE__*/_jsxs("div", {
              children: [providerCostLabel, ": ", priceUtils.formatPrice(seatPrice - markup)]
            }), /*#__PURE__*/_jsxs("div", {
              children: [priceInfoTotalLabel, ": ", priceUtils.formatPrice(seatPrice)]
            })]
          }),
          placement: isFirstTooltip ? 'bottom' : undefined,
          title: priceLabel,
          children: /*#__PURE__*/_jsx("button", {
            "aria-label": priceLabel,
            className: cx(resetButtonStyles, infoIconButtonStyles),
            type: "button",
            children: /*#__PURE__*/_jsx(Info, {
              color: vars.colors.signal.information.foreground,
              size: 20,
              weight: "fill"
            })
          })
        })
      })]
    })
  });
};
export default SummaryPriceRow;
//# sourceMappingURL=SummaryPriceRow.js.map